import { useState, useEffect } from "react";
import { getErrorMessage } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Briefcase, Calendar, Plus, Pencil, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface ExperienceProject {
  id?: string;
  experience_id?: string;
  title: string;
  description: string;
  dates: string | null;
  tech: string[];
  sort_order: number;
}

interface ExperienceItem {
  id: string;
  company: string;
  role: string;
  dates: string;
  logo_url: string | null;
  sort_order: number;
  projects: ExperienceProject[];
}

interface ProjectForm {
  id?: string;
  title: string;
  description: string;
  dates: string;
  techText: string;
}

/**
 * Company logo with a graceful fallback.
 *
 * A logo_url can point at a file that no longer exists (renamed asset, logo not yet
 * uploaded). Without an error handler that renders as a broken-image icon, so failures
 * fall back to the same placeholder used when no logo is set at all.
 */
const CompanyLogo = ({ company, logoUrl }: { company: string; logoUrl: string | null }) => {
  const [failed, setFailed] = useState(false);

  if (!logoUrl || failed) {
    return (
      <div className="p-2 sm:p-3 rounded-lg bg-primary/10">
        <Briefcase className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
      </div>
    );
  }

  return (
    <img
      src={logoUrl}
      alt={`${company} logo`}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 rounded-lg object-contain"
    />
  );
};

const Experience = () => {
  const { t } = useLanguage();
  const { isAdmin, user } = useAuth();
  const [experiences, setExperiences] = useState<ExperienceItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedExp, setSelectedExp] = useState<ExperienceItem | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [dates, setDates] = useState("");
  const [sortOrder, setSortOrder] = useState("0");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [projects, setProjects] = useState<ProjectForm[]>([]);

  const fetchExperiences = async () => {
    const { data: expData, error: expErr } = await supabase
      .from("experiences")
      .select("*")
      .order("sort_order", { ascending: true });

    if (expErr || !expData) {
      setLoading(false);
      return;
    }

    const { data: projData } = await supabase
      .from("experience_projects")
      .select("*")
      .order("sort_order", { ascending: true });

    const merged = expData.map((e) => ({
      ...e,
      projects: (projData || []).filter((p) => p.experience_id === e.id),
    })) as ExperienceItem[];

    setExperiences(merged);
    setLoading(false);
  };

  useEffect(() => {
    fetchExperiences();
  }, []);

  const resetForm = () => {
    setCompany("");
    setRole("");
    setDates("");
    setSortOrder("0");
    setLogoFile(null);
    setLogoPreview(null);
    setProjects([]);
    setSelectedExp(null);
  };

  const openAddDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (exp: ExperienceItem) => {
    setSelectedExp(exp);
    setCompany(exp.company);
    setRole(exp.role);
    setDates(exp.dates);
    setSortOrder(String(exp.sort_order));
    setLogoPreview(exp.logo_url);
    setLogoFile(null);
    setProjects(
      exp.projects.map((p) => ({
        id: p.id,
        title: p.title,
        description: p.description,
        dates: p.dates || "",
        techText: (p.tech || []).join(", "),
      }))
    );
    setDialogOpen(true);
  };

  const openDeleteDialog = (exp: ExperienceItem) => {
    setSelectedExp(exp);
    setDeleteDialogOpen(true);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async (file: File): Promise<string | null> => {
    const ext = file.name.split(".").pop();
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage
      .from("experience-logos")
      .upload(fileName, file);
    if (error) {
      console.error("Upload error:", error);
      return null;
    }
    const { data } = supabase.storage
      .from("experience-logos")
      .getPublicUrl(fileName);
    return data.publicUrl;
  };

  const deleteStorageFile = async (url: string) => {
    try {
      const parts = url.split("/experience-logos/");
      if (parts[1]) {
        await supabase.storage.from("experience-logos").remove([parts[1]]);
      }
    } catch (e) {
      console.error("Error deleting file:", e);
    }
  };

  const addProjectRow = () => {
    setProjects([
      ...projects,
      { title: "", description: "", dates: "", techText: "" },
    ]);
  };

  const updateProjectField = (idx: number, field: keyof ProjectForm, value: string) => {
    setProjects(projects.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));
  };

  const removeProjectRow = (idx: number) => {
    setProjects(projects.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!company.trim() || !role.trim() || !dates.trim()) {
      toast.error("Company, Role, dan Dates wajib diisi");
      return;
    }

    setSaving(true);
    try {
      let logoUrl = selectedExp?.logo_url || null;

      if (logoFile) {
        if (selectedExp?.logo_url) {
          await deleteStorageFile(selectedExp.logo_url);
        }
        logoUrl = await uploadLogo(logoFile);
        if (!logoUrl) {
          toast.error("Gagal mengupload logo");
          setSaving(false);
          return;
        }
      }

      const expData = {
        company: company.trim(),
        role: role.trim(),
        dates: dates.trim(),
        logo_url: logoUrl,
        sort_order: parseInt(sortOrder) || 0,
      };

      let experienceId = selectedExp?.id;

      if (selectedExp) {
        const { error } = await supabase
          .from("experiences")
          .update(expData)
          .eq("id", selectedExp.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("experiences")
          .insert({ ...expData, created_by: user?.id })
          .select()
          .single();
        if (error) throw error;
        experienceId = data.id;
      }

      // Sync projects: delete removed ones, upsert remaining
      if (selectedExp) {
        const keptIds = projects.filter((p) => p.id).map((p) => p.id);
        const toDelete = selectedExp.projects
          .filter((p) => p.id && !keptIds.includes(p.id))
          .map((p) => p.id);
        if (toDelete.length > 0) {
          await supabase
            .from("experience_projects")
            .delete()
            .in("id", toDelete);
        }
      }

      for (let i = 0; i < projects.length; i++) {
        const p = projects[i];
        if (!p.title.trim()) continue;
        const techArr = p.techText
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        const projData = {
          experience_id: experienceId,
          title: p.title.trim(),
          description: p.description.trim(),
          dates: p.dates.trim() || null,
          tech: techArr,
          sort_order: i,
        };
        if (p.id) {
          await supabase
            .from("experience_projects")
            .update(projData)
            .eq("id", p.id);
        } else {
          await supabase.from("experience_projects").insert(projData);
        }
      }

      toast.success(
        selectedExp ? "Experience berhasil diperbarui" : "Experience berhasil ditambahkan"
      );
      setDialogOpen(false);
      resetForm();
      fetchExperiences();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedExp) return;
    try {
      if (selectedExp.logo_url) {
        await deleteStorageFile(selectedExp.logo_url);
      }
      const { error } = await supabase
        .from("experiences")
        .delete()
        .eq("id", selectedExp.id);
      if (error) throw error;
      toast.success("Experience berhasil dihapus");
      setDeleteDialogOpen(false);
      setSelectedExp(null);
      fetchExperiences();
    } catch (error) {
      toast.error(getErrorMessage(error, "Gagal menghapus experience"));
    }
  };

  const displayedExperiences = showAll ? experiences : experiences.slice(0, 1);

  return (
    <section id="experience" className="py-12 sm:py-16 md:py-20 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="text-center mb-8 sm:mb-10 md:mb-12 animate-fade-up">
          <h2 className="text-3xl sm:text-4xl font-bold mb-3 sm:mb-4">{t.experience.title}</h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            {t.experience.subtitle}
          </p>
        </div>

        {isAdmin && (
          <div className="flex justify-center mb-6">
            <Button onClick={openAddDialog} className="gap-2">
              <Plus className="h-4 w-4" />
              Tambah Experience
            </Button>
          </div>
        )}

        {loading ? (
          <div className="max-w-4xl mx-auto space-y-6">
            {[1, 2].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6 space-y-3">
                  <div className="h-16 w-16 bg-muted rounded" />
                  <div className="h-4 bg-muted rounded w-3/4" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : experiences.length === 0 ? (
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-soft animate-fade-up">
              <CardContent className="p-6 sm:p-8 md:p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
                    <Briefcase className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Belum ada data experience</h3>
                  <p className="text-sm text-muted-foreground">
                    Data pengalaman kerja belum ditambahkan.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-6 sm:space-y-7 md:space-y-8">
            {displayedExperiences.map((exp) => (
              <Card
                key={exp.id}
                className="shadow-soft hover:shadow-medium transition-all duration-300 animate-fade-in hover-glow"
              >
                <CardHeader className="pb-4 sm:pb-5 md:pb-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="flex-shrink-0">
                      <CompanyLogo company={exp.company} logoUrl={exp.logo_url} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-lg sm:text-xl md:text-2xl mb-1 sm:mb-2">
                            {exp.company}
                          </CardTitle>
                          <p className="text-base sm:text-lg font-medium text-foreground">
                            {exp.role}
                          </p>
                          <p className="text-xs sm:text-sm text-muted-foreground flex items-center gap-1.5 sm:gap-2 mt-1">
                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                            <span className="truncate">{exp.dates}</span>
                          </p>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => openEditDialog(exp)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:text-destructive"
                              onClick={() => openDeleteDialog(exp)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                {exp.projects.length > 0 && (
                  <CardContent className="space-y-4 sm:space-y-5 md:space-y-6">
                    {exp.projects.map((project, pIndex) => (
                      <div
                        key={project.id || pIndex}
                        className="border-l-2 border-primary/30 pl-3 sm:pl-4 py-2"
                      >
                        <h4 className="font-semibold text-base sm:text-lg mb-1.5 sm:mb-2">
                          {project.title}
                        </h4>
                        {project.dates && (
                          <p className="text-[10px] sm:text-xs text-muted-foreground mb-2 flex items-center gap-1">
                            <Calendar className="h-3 w-3 flex-shrink-0" />
                            <span>{project.dates}</span>
                          </p>
                        )}
                        {project.description && (
                          <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
                            {project.description}
                          </p>
                        )}
                        {project.tech && project.tech.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            {project.tech.map((tech, tIndex) => (
                              <Badge
                                key={tIndex}
                                variant="secondary"
                                className="text-[10px] sm:text-xs"
                              >
                                {tech}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            ))}

            {experiences.length > 1 && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="secondary"
                  onClick={() => setShowAll(!showAll)}
                  className="min-w-[200px] hover:bg-primary hover:text-primary-foreground transition-colors"
                >
                  {showAll ? t.portfolio.showLess : t.portfolio.showMore}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedExp ? "Edit Experience" : "Tambah Experience Baru"}
            </DialogTitle>
            <DialogDescription>
              {selectedExp
                ? "Perbarui informasi pengalaman kerja"
                : "Isi detail pengalaman kerja yang ingin ditambahkan"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label htmlFor="exp-company">Company *</Label>
              <Input
                id="exp-company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Masukkan nama perusahaan"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-role">Role *</Label>
              <Input
                id="exp-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                placeholder="Masukkan posisi/role"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="exp-dates">Dates *</Label>
                <Input
                  id="exp-dates"
                  value={dates}
                  onChange={(e) => setDates(e.target.value)}
                  placeholder="Masukkan periode"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="exp-sort">Urutan</Label>
                <Input
                  id="exp-sort"
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  placeholder="Masukkan urutan"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="exp-logo">Logo Perusahaan</Label>
              <Input
                id="exp-logo"
                type="file"
                accept="image/*"
                onChange={handleLogoChange}
              />
              {logoPreview && (
                <div className="mt-2">
                  <img
                    src={logoPreview}
                    alt="Preview"
                    className="h-16 w-16 object-contain border rounded-lg p-1"
                  />
                </div>
              )}
            </div>

            <div className="space-y-3 pt-2 border-t">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Projects</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addProjectRow}
                  className="gap-1"
                >
                  <Plus className="h-3.5 w-3.5" /> Tambah Project
                </Button>
              </div>

              {projects.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  Belum ada project. Klik "Tambah Project" untuk menambahkan.
                </p>
              )}

              {projects.map((p, idx) => (
                <Card key={idx} className="p-3 space-y-2 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Project #{idx + 1}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive"
                      onClick={() => removeProjectRow(idx)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  <Input
                    placeholder="Masukkan judul project"
                    value={p.title}
                    onChange={(e) => updateProjectField(idx, "title", e.target.value)}
                  />
                  <Input
                    placeholder="Masukkan periode project"
                    value={p.dates}
                    onChange={(e) => updateProjectField(idx, "dates", e.target.value)}
                  />
                  <Textarea
                    placeholder="Masukkan deskripsi"
                    value={p.description}
                    onChange={(e) => updateProjectField(idx, "description", e.target.value)}
                    rows={2}
                  />
                  <Input
                    placeholder="Masukkan tech stack (pisah dengan koma)"
                    value={p.techText}
                    onChange={(e) => updateProjectField(idx, "techText", e.target.value)}
                  />
                </Card>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Batal
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Menyimpan..." : "Simpan"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus Experience?</AlertDialogTitle>
            <AlertDialogDescription>
              Tindakan ini tidak bisa dibatalkan. Experience "{selectedExp?.company}" beserta
              semua project di dalamnya akan dihapus permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export default Experience;
